'use client';

import { BigNum, SpotMarketConfig } from '@drift-labs/sdk';
import { MarketId } from '@drift/common';
import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import { notify } from 'src/utils/notifications';
import captureException from 'src/utils/captureException';
import TransactionErrorHandler from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { updateAfterTx } from 'src/stores/DriftStore/helpers';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useDriftClient from 'src/hooks/useDriftClient';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignDriftActionWithFallback } from 'src/hooks/wallets/useSponsoredSignDriftActionWithFallback';

export function useTransferCollateral() {
	const driftClient = useDriftClient();
	const walletAdapter = useDriftStore((s) => s.wallet.current.adapter);
	const accountsGet = useDriftAccountsStore((s) => s.get);
	const getTxParams = useGetTxParams();

	// TODO: abstract into @drift/common
	const transferCollateralAction = useCallback(
		async (
			wallet: UiEnhancedIWalletV2,
			amount: BigNum,
			spotMarket: SpotMarketConfig,
			fromAccountUserId: number,
			toAccountUserId: number
		) => {
			invariant(driftClient, 'driftClient is required');
			const ix = await driftClient.getTransferDepositIx(
				amount.val,
				spotMarket.marketIndex,
				fromAccountUserId,
				toAccountUserId
			);

			await wallet.signAndSendTransaction(
				ix,
				getTxParams({
					marketId: MarketId.createSpotMarket(spotMarket.marketIndex),
					simulatedComputeUnitMultiplier: Env.computeUnitBufferMultiplier + 0.3,
				})
			);
		},
		[driftClient, getTxParams]
	);

	const wrappedTransferCollateral = useSponsoredSignDriftActionWithFallback(
		transferCollateralAction
	);

	const transferCollateral = useCallback(
		async (
			amount: BigNum,
			spotMarket: SpotMarketConfig,
			fromAccountKey: string,
			toAccountKey: string
		) => {
			if (!driftClient || !walletAdapter) return;

			const accountsState = accountsGet();
			const fromAccount = accountsState.accounts[fromAccountKey];
			const toAccount = accountsState.accounts[toAccountKey];

			if (!fromAccount?.client || !toAccount?.client) {
				return;
			}

			const fromUser = fromAccount.client;

			const toastId = `transfer-${amount.toString()}-${Date.now()}`;

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				await wrappedTransferCollateral(
					amount,
					spotMarket,
					fromAccount.userId,
					toAccount.userId
				);

				notify({
					type: 'success',
					id: toastId,
					updatePrevious: true,
				});

				updateAfterTx(driftClient, fromUser);

				return true;
			} catch (e: any) {
				captureException(e);
				TransactionErrorHandler.handleError({
					error: e,
					toastId,
					wallet: walletAdapter,
				});
				return false;
			}
		},
		[driftClient, walletAdapter, accountsGet, wrappedTransferCollateral]
	);

	return { transferCollateral };
}
